import { Component, EventEmitter, input, Input, InputSignal, Output, SimpleChanges } from '@angular/core';
import { EzBaseComponent } from '@template-ui/components/ez-base.component';
import { CommonModule } from '@angular/common';
import { InputConfig, InputWidthType } from '@template-ui/types/ez-form.types';
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { DocMeta } from "@template-ui/decorators";

/**
 * Given a bunch of checkbox label/ischecked objects,
 * Create a list of checkboxes in multiple columns
 * by dividing them up as evenly as possible into numColumns.
 * Internally this uses a single bits-dynamic-form for each column
 */
@DocMeta({
  title: "Checkbox Columns",
  category: "Components",
  showcaseComponentNames: ["CheckboxColumnsSampleComponent"],
  relatedTypesFromFiles: ["@template-ui/types/ez-form.types.ts"]
})
@Component({
    selector: "app-checkbox-columns",
    imports: [
        // Angular stuff
        CommonModule,
        EzFormComponent
    ],
    templateUrl: "./checkbox-columns.component.html",
    styleUrl: "./checkbox-columns.component.scss"
})
export class CheckboxColumnsComponent extends EzBaseComponent {
  /**
   * If specified, a title over the checkboxes, styled with the 'title' class
   * which could be inherited from other title rules
   */
  //@Input() title: string = ""; never used and a bad name cuz it conflicts with an html title property

  /**
   * An array of label/IsChecked types
   */
  @Input() checkboxes: { label: string; isChecked: boolean }[] = [];

  /**
   * Sets the readOnly property on each checkbox in the form
   */
  @Input() readOnly: boolean = true;

  /**
   * Additional classes to apply to the outermost container
   */
  @Input() containerClass?: string = "";

  /**
   * Optional class(es) to apply to the columns.
   */
  @Input() columnClass?: string = "";


  /**
   * Set the width of the columns, default is `fill` in which case
   * the columns will be divided equally in the container.
   */
  @Input() columnWidth: InputWidthType = 'fill';

  /**
   * Specify the number of columns that the checkboxes should
   * span accross
   */
  @Input() numColumns: number = 1;

  /**
   * Order can be:
   * - arrayOrder - the order in which the checkboxes were defined (default)
   * - alphabetical - sort by the label
   */
  @Input() orderBy: "arrayOrder" | "alphabetical" = "arrayOrder";

  /**
   * Emits when a checkbox is toggled.
   */
  @Output() checkboxToggled = new EventEmitter<{ label: string; isChecked: boolean }>();

  /**
   * @internal
   */
  protected columns: { label: string; isChecked: boolean }[][] = [];

  /**
   * @internal
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    //let checkboxes = changes["checkboxes"].currentValue;

    //if (checkboxes && checkboxes.length > 0) {
      this.setColumns();
    //}
  }

    /**
   * @internal
   */
  protected dynamicFormConfigs: InputConfig[][] = [];
  protected dynamicFormValues: Record<string, boolean>[] = [];


  private setColumns() {
    this.columns = [];
    //this.dynamicFormConfigs = [][this.numColumns];

    const totalCheckboxes = this.checkboxes?.length ?? 0;

    /* NOTE: This logic may not always produce what you expect.
       Example: 9 checkboxes with 4 columns will result in only 3 columns
              cuz there's no more to put in the 4th column.
              Another way to do it would have 3 rows in the first column
              and the remaining 6 divided up in the last 3 columns, but that would be weird as well.

       This will happen anytime that the number of checkboxes/(cols-1) has a remainder of 0
       9 mod (4-1) = 0
    */
    const numCbPerCol = Math.ceil(totalCheckboxes / this.numColumns);

    // if(totalCheckboxes % (this.numColumns-1) == 0)
    // {
    //   // Do something different?
    // }

    // If necessary, sort the array
    let sortedCheckboxes = this.checkboxes;
    if (this.orderBy == "alphabetical") {
      sortedCheckboxes = [...this.checkboxes].sort((a, b) => (a.label > b.label ? 1 : -1));
    }

    // Divide them in column groups
    this.dynamicFormConfigs = [];
    this.dynamicFormValues = [];
    for (let i = 0; i < this.numColumns; i++) {
      const start = i * numCbPerCol;
      const end = start + numCbPerCol;
      this.columns[i] = sortedCheckboxes.slice(start, end);
      //this.dynamicFormConfigs.push([]);

      // Convert each column to a dynamic-form config
      this.dynamicFormConfigs[i] = [];
      this.dynamicFormValues[i] = {};
      this.columns[i].forEach((cb: { label: string; isChecked: boolean }) => {
        const ic: InputConfig = {
          type: "checkbox",
          label: cb.label,
          value: cb.isChecked,
          name: cb.label,
          readOnly: this.readOnly,
          width: this.columnWidth
        };
        this.dynamicFormConfigs[i].push(ic);
        this.dynamicFormValues[i][cb.label] = cb.isChecked;
      });
    }
  }

  ngOnInit() {
    // Order the checkboxes and divide them up into numColumns columns
    this.setColumns();
  }

  protected onCheckboxChange(event: { name: string; checked: boolean }): void {
    this.checkboxToggled.emit({ label: event.name, isChecked: event.checked });
  }
}
